import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import AsyncSelect from "react-select/async";
import _ from "lodash";
import {getSelectDropdownStyles} from "../../Components/Dropdowns/selectDropdownStyles";
import {searchCanonicalStores} from "./storeLinkage";

export const SEARCH_DEBOUNCE_MS = 300;
export const MIN_SEARCH_LENGTH = 2;
export const CANONICAL_STORE_SELECTED_TEST_ID = 'canonical-store-selected';
export const CANONICAL_STORE_SEARCH_ERROR_TEST_ID = 'canonical-store-search-error';
export const canonicalStoreOptionTestId = (canonicalStoreId) => `canonical-store-option-${canonicalStoreId}`;

const addressLine = ({address, city, state, postalCode}) =>
    [address, city, [state, postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');

const toOption = (store) => ({value: store.canonicalStoreId, store});

const formatStoreOption = ({value, store}, {context}) => {
    const bannerName = store.bannerName || 'No banner';
    if (context === 'value') return <span data-testid={CANONICAL_STORE_SELECTED_TEST_ID}>{bannerName} ({value})</span>;
    return <div className={"canonical-store-option"} data-testid={canonicalStoreOptionTestId(value)}>
        <div className={"canonical-store-option-banner"}>{bannerName}</div>
        <div className={"canonical-store-option-address"}>{addressLine(store)}</div>
        <div className={"canonical-store-option-id"}>{value}</div>
    </div>;
};

const noStoreOptionsMessage = ({inputValue}) =>
    inputValue.trim().length < MIN_SEARCH_LENGTH ? `Type at least ${MIN_SEARCH_LENGTH} characters` : 'No stores found';

const CanonicalStoreSearchSelect = ({
                                        inputId,
                                        version,
                                        excludeCanonicalStoreId,
                                        value,
                                        onChange,
                                        isDisabled = false,
                                        className,
                                        height = 40,
                                        width = '100%',
                                    }) => {
    const [searchError, setSearchError] = useState(null);

    const loadStoreOptions = useMemo(() => _.debounce((query, callback) => {
        if (query.trim().length < MIN_SEARCH_LENGTH) {
            callback([]);
            return;
        }
        searchCanonicalStores({version, query, excludeCanonicalStoreId})
            .then(stores => {
                setSearchError(null);
                callback(stores.map(toOption));
            })
            .catch(error => {
                setSearchError(`Store search failed: ${error.message}`);
                callback([]);
            });
    }, SEARCH_DEBOUNCE_MS), [version, excludeCanonicalStoreId]);

    useEffect(() => () => loadStoreOptions.cancel(), [loadStoreOptions]);

    return <div className={"canonical-store-select"}>
        <AsyncSelect
            key={version}
            inputId={inputId}
            className={className}
            classNamePrefix={"select-dropdown"}
            styles={getSelectDropdownStyles({height, width})}
            placeholder={'Search by banner, address, city, state, zip or id'}
            loadOptions={loadStoreOptions}
            cacheOptions={false}
            defaultOptions={false}
            value={value}
            onChange={onChange}
            formatOptionLabel={formatStoreOption}
            noOptionsMessage={noStoreOptionsMessage}
            isClearable
            isDisabled={isDisabled}
        />
        {searchError &&
            <div className={"canonical-store-search-error"} data-testid={CANONICAL_STORE_SEARCH_ERROR_TEST_ID}>{searchError}</div>}
    </div>;
};

CanonicalStoreSearchSelect.propTypes = {
    inputId: PropTypes.string.isRequired,
    version: PropTypes.string,
    excludeCanonicalStoreId: PropTypes.string,
    value: PropTypes.shape({
        value: PropTypes.string.isRequired,
        store: PropTypes.object.isRequired,
    }),
    onChange: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    className: PropTypes.string,
    height: PropTypes.number,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CanonicalStoreSearchSelect;
